<template>
    <div class="container">
        <section class="section">
            <div class="container">
                <h1 class="title">Talks</h1>
                <div class="columns is-multiline">
                    <div class="column is-4-widescreen is-6-tablet" v-for="talk in talks" v-bind:key="talk.title">
                        <div class="card">
                            <div class="card-image">
                                <iframe width="100%" height="300" :src="talk.youtubeEmbed" frameborder="0"
                                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                    allowfullscreen></iframe>
                            </div>
                            <div class="card-content">
                                <div class="media">
                                    <div class="media-left">
                                        <span class="icon">
                                            <i class="fas fa-comment-alt"></i>
                                        </span>
                                    </div>
                                    <div class="media-content">
                                        <div class="title is-6" style="min-height: 3.5em">
                                            <a :href="talk.youtubeLink">{{
                                                talk.title
                                            }}</a>
                                        </div>
                                        <div class="content">
                                            <b>{{ talk.byline }}</b>
                                            <br />
                                            <div>{{ talk.venue }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <section class="section">
            <div class="container">
                <div class="columns">
                    <div class="column">
                        <h1 class="title">Publications</h1>
                        <h2 class="subtitle">
                            If the BossDB data archive service has been useful to your research, please cite
                            <a href="https://doi.org/10.3389/fninf.2022.828787" target="_blank"><b>Hider et al., 2022.</b></a>
                        </h2>
                        <div v-for="(pub, idx) in publications" :key="pub.title">
                        <h2
                            v-if="!pub.featured && (idx === 0 || publications[idx - 1].year !== pub.year)"
                            class="year-header"
                            >
                            {{ pub.year }}
                        </h2>
                            <div class="card">        
                                <div class="card-content">
                                    <div class="media">
                                        <div class="media-left">
                                            <span class="icon">
                                                <i class="fas fa-book-open"></i>
                                            </span>
                                        </div>
                                        <div class="media-content">
                                            <div :class="`title ` + (pub.featured ? `is-4` : `is-6`)">
                                                <a :href="pub.link">
                                                    {{ pub.title }}
                                                </a>
                                            </div>
                                            <div class="content">
                                                {{ pub.authors }}
                                                <br />
                                                <div v-html="pub.contents" class="
                                                        small-abstract
                                                    "></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <hr v-if="pub.featured" class="featured-divider" />
                            <br />
                        </div>
                        </div>
                </div>
            </div>
        </section>
        <section class="section">
            <div class="container">
                <div class="columns">
                    <div class="column">
                        <h1 class="title">Posters</h1>
                    </div>
                </div>
                <div class="columns is-multiline">
                    <div class="column is-4-desktop is-6-tablet" v-for="pub in posters" v-bind:key="pub.title">
                        <div class="card">
                            <div class="card-image" v-if="pub.link">
                            <a :href="pub.link" target="_blank" rel="noopener" class="poster-preview-link">
                                <img
                                v-if="pub.thumb"
                                class="poster-thumb"
                                :src="pub.thumb"
                                :alt="pub.title"
                                loading="lazy"
                                decoding="async"
                                />

                                <div v-else class="poster-thumb-placeholder">
                                <span class="icon is-large">
                                    <i class="fas fa-file-pdf fa-2x"></i>
                                </span>
                                <div class="poster-placeholder-text">Open poster (PDF)</div>
                                </div>
                            </a>
                            </div>
                            <div class="card-content">
                                <div class="media">
                                    <div class="media-left">
                                        <span class="icon">
                                            <i class="
                                                    fas
                                                    fa-chalkboard-teacher
                                                "></i>
                                        </span>
                                    </div>
                                    <div class="media-content">
                                        <div class="title is-6">
                                            <a v-if="pub.link" :href="pub.link" target="_blank" rel="noopener">{{ pub.title }}</a>

                                            <div v-else>{{ pub.title }}</div>
                                        </div>
                                        <div class="content">
                                            <b>{{ pub.authors }},</b>
                                            {{ pub.venue }}
                                            <br />
                                            <div v-html="pub.contents"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <br />
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <div class="section">
            <p>
                <Acknowledgements />
            </p>
        </div>
        <div class="section">
            <h1 class="title">Contact Us</h1>
            <p>
                For any questions, suggestions or collaboration interests please
                contact:
                <a href="mailto:info@bossdb.org">info@bossdb.org</a>
            </p>
        </div>
    </div>
</template>


<script>
import posters from "@/posters.js";
import talks from "@/talks.js";
import publications from "@/publications.js";
import showdown from "showdown";
import Acknowledgements from "./Acknowledgements.vue";

export default {
    data() {
        let converter = new showdown.Converter({
            simplifiedAutoLink: true,
        });
        return {
            posters,
            talks,
            // Sort by 'date' field, which is a JS Date object:
            publications: publications
                .sort((a, b) => {
                    // Sort by date, prioritizing `featured` publications at
                    // the top:
                    if (a.featured && !b.featured) {
                        return -1;
                    } else if (!a.featured && b.featured) {
                        return 1;
                    } else {
                        return b.date - a.date;
                    }
                })
                .map((event) => {
                    return {
                        ...event,
                        contents: converter.makeHtml(event.contents.join("\n\n")),
                    };
                }),
        };
    },
    components: { Acknowledgements }
};
</script>


<style lang="scss" scoped>
.has-equal-height {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.small-abstract {
    font-size: 0.8em;
    border-left: 2px solid #ccc;
    padding-left: 0.5em;
    margin-top: 2em;
}

.featured-divider {
  border: none;
  border-top: 5px solid #bdbdbd;
  margin: 2rem 0 -2rem 0;
}

.year-header {
  margin-top: 2.5rem;
  margin-bottom: 1rem;
  font-size: 1.35rem;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 0.25rem;
}

.poster-preview-link {
  display: block;
}

/* poster thumbnail*/
.poster-thumb {
  width: 100%;
  height: 200px; 
}


</style>
